import React, { useState, useEffect } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetTrigger } from "@/components/ui/sheet";
import { Search, Menu, X } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";

import AuthModal from "@/modules/storefront/components/AuthModal";
import CmsFooter from "@/modules/storefront/components/CmsFooter";
import PushPrompt from "@/modules/storefront/components/PushPrompt";
import FloatingActions from "@/modules/storefront/components/FloatingActions";
import MobileBottomNav from "@/modules/storefront/components/MobileBottomNav";
import MegaMenu from "@/modules/storefront/components/MegaMenu";
import LeadGate from "@/modules/storefront/components/LeadGate";
import PhoneRequiredModal from "@/modules/storefront/components/PhoneRequiredModal";
import MobileSideSheet from "@/modules/storefront/components/MobileSideSheet";
import DesktopHeader from "@/modules/storefront/components/DesktopHeader";
import MobileHeaderIcons from "@/modules/storefront/components/MobileHeader";
import CartDrawer from "@/modules/storefront/components/CartDrawer";
import { QuickAddProvider } from "@/contexts/QuickAddContext";
import { useAuthModal } from "@/contexts/AuthModalContext";

export default function StorefrontLayout() {
  const { isAuthenticated, isCustomer, logout } = useAuth();
  const { itemCount } = useCart();
  const { open: authOpen, openAuthModal, closeAuthModal } = useAuthModal();
  const [searchQ, setSearchQ] = useState("");
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [taxonomyData, setTaxonomyData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/nav/taxonomies`)
      .then(r => r.json())
      .then(d => setTaxonomyData(d.taxonomies || []))
      .catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQ.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQ)}`);
      setMobileSearchOpen(false);
      setSearchQ("");
    }
  };

  return (
    <QuickAddProvider>
    <div className="min-h-screen flex flex-col overflow-x-hidden bg-white">
      <header className="nc-liquid-glass fixed top-0 left-0 right-0 z-50 w-full max-w-[100vw]">
        <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6">
          <div className="flex items-center h-14 sm:h-16 gap-2 w-full">
            {/* Mobile hamburger */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden -ml-2 min-w-[44px] min-h-[44px] nc-glass-icon hover:bg-black/5" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <MobileSideSheet />
            </Sheet>

            {/* Logo */}
            <Link to="/" className="flex items-center shrink-0" data-testid="logo-link">
              <span className="text-lg sm:text-xl md:text-2xl font-extrabold tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                <span style={{ color: "#FFB800" }}>Nirvana</span>
                <span style={{ color: "#0f0f14" }}>Comfort</span>
              </span>
            </Link>

            {/* Mobile spacer */}
            <div className="flex-1 md:hidden" />

            {/* Desktop Header */}
            <DesktopHeader
              searchQ={searchQ} setSearchQ={setSearchQ} handleSearch={handleSearch}
              megaOpen={megaOpen} setMegaOpen={setMegaOpen}
              isAuthenticated={isAuthenticated} isCustomer={isCustomer} logout={logout}
              itemCount={itemCount} onAuthOpen={() => openAuthModal()}
            />

            {/* Mobile Icons */}
            <MobileHeaderIcons itemCount={itemCount} />
          </div>
        </div>

        {/* Mobile search bar */}
        {mobileSearchOpen && (
          <div className="md:hidden px-4 py-3 animate-fade-in" style={{ borderTop: "1px solid rgba(0,0,0,0.06)", background: "#ffffff" }}>
            <form onSubmit={handleSearch} className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#6b6478" }} />
                <Input data-testid="mobile-search-input" autoFocus placeholder="Search products..."
                  value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                  className="pl-9 h-10 rounded-full text-sm w-full"
                  style={{ background: "rgba(0,0,0,0.04)", border: "1px solid rgba(0,0,0,0.1)", color: "#1A1A1A" }} />
              </div>
              <button type="button" onClick={() => { setMobileSearchOpen(false); setSearchQ(""); }}
                className="p-2 min-w-[40px] min-h-[40px] flex items-center justify-center" style={{ color: "#6b6478" }}>
                <X className="w-5 h-5" />
              </button>
            </form>
          </div>
        )}
      </header>

      <MegaMenu open={megaOpen} onClose={() => setMegaOpen(false)} taxonomyData={taxonomyData} />

      <main className="flex-1 pb-16 md:pb-0 pt-[68px] sm:pt-[72px] storefront-zoom">
        <Outlet />
      </main>

      <AuthModal open={authOpen} onClose={closeAuthModal} />
      <PhoneRequiredModal />
      <LeadGate />
      <PushPrompt />
      <CmsFooter />
      <FloatingActions />
      <MobileBottomNav
        onSearchOpen={() => setMobileSearchOpen(true)}
        onAuthOpen={() => openAuthModal()}
      />
      <CartDrawer />
    </div>
    </QuickAddProvider>
  );
}
